import React, { useState } from "react";
import { Box, makeStyles, FormGroup, Typography } from "@material-ui/core";
import AppButton from "components/CustomButtons/AppButton";
import AppColor from "utilities/AppColor";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  updateDocument,
  fetchWorkerDetail,
  updateStatusFileDocuments,
} from "store/slices/worker";
import { getAuthInfo } from "store/slices/auth";
import { getCompanyConfig } from "store/slices/company";
import moment from "moment";
import { showToast } from "store/slices/toast";
import StaffUploadDocumentModal from "../modal/StaffUploadDocumentModal";
import DocumentVersionsModal from "../../Client/form/DocumentVersionsModal";
import snakecaseKeys from "snakecase-keys";
import { SYSTEM_ROLE } from "config/AppConfig";
import { FILE_STATUS } from "config/AppConfig";

const useStyles = makeStyles(() => ({
  docItem: {
    display: "flex",
    alignItems: "center",
    padding: "10px 0",
    borderBottom: "1px solid #F3F4F6",
    fontSize: 12,
  },
  docIcon: {
    fontSize: 18,
    color: "#0D9488",
    marginRight: 10,
  },
  docName: {
    color: "#374151",
    fontWeight: 500,
    display: "inline",
  },
  donePill: {
    background: "#CCFBF1",
    color: "#0F766E",
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    padding: "2px 7px",
    borderRadius: 5,
    fontSize: 11,
    fontWeight: 500,
    height: 24,
    boxSizing: "border-box",
    whiteSpace: "nowrap",
  },
  warningPill: {
    background: "#FEF3C7",
    color: "#92400E",
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    padding: "2px 7px",
    borderRadius: 5,
    fontSize: 11,
    fontWeight: 500,
    height: 24,
    boxSizing: "border-box",
    whiteSpace: "nowrap",
  },
  overduePill: {
    background: "#FEE2E2",
    color: "#991B1B",
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    padding: "2px 7px",
    borderRadius: 5,
    fontSize: 11,
    fontWeight: 500,
    height: 24,
    boxSizing: "border-box",
    whiteSpace: "nowrap",
  },
  uploadArea: {
    border: "1.5px dashed #E5E7EB",
    borderRadius: 6,
    padding: 20,
    textAlign: "center",
    marginTop: 16,
    cursor: "pointer",
  },
  uploadIcon: {
    fontSize: 24,
    color: "#9CA3AF",
    display: "block",
    marginBottom: 6,
  },
  uploadText: {
    fontSize: 12,
    color: "#6B7280",
  },
  uploadSubtext: {
    fontSize: 11,
    color: "#9CA3AF",
  },
  control: {
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 20,
    marginBottom: 20,
  },
  highlightRow: {
    backgroundColor: "#EFF6FF",
  },
}));

const DocumentsTab = ({ staffDetail, onChange }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const authInfo = useSelector(getAuthInfo);
  const companyConfig = useSelector(getCompanyConfig) || {};
  const isAdmin = authInfo?.role === SYSTEM_ROLE.COMPANY;

  const [localUploads, setLocalUploads] = useState({});
  const [editingContext, setEditingContext] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletedFiles, setDeletedFiles] = useState({}); // Track deleted server files: { [documentsType]: ids[] }

  // const [showViewer, setShowViewer] = useState(false);
  // const [viewerFile, setViewerFile] = useState(null);

  const [showVersionsModal, setShowVersionsModal] = useState(false);
  const [versionsData, setVersionsData] = useState([]);
  const [versionsLocalFiles, setVersionsLocalFiles] = useState([]);
  const [versionsMetadata, setVersionsMetadata] = useState(null);

  const hiddenFileInputRef = React.useRef(null);
  const globalFileInputRef = React.useRef(null);

  const { id = null } = staffDetail || {};

  const documentCategories = [
    {
      key: "workerNdisWorkerScreeningCheckResponseList",
      label: t("document.ndisWorkerScreeningCheck"),
      fileArrayKey: "ndis_worker_screening_check",
      needsExpiry: true,
      expireKey: "ndisWorkerScreeningCheckExpireDate",
      fileNameKey: "ndisWorkerScreeningCheckFile",
      fileLinkKey: "ndisWorkerScreeningCheckFileAws3Link",
      documentType: "WORKER_NDISWORKER_SCREENING_CHECK"
    },
    {
      key: "workerNdisOrientationModuleResponseList",
      label: t("trainingTag.workerNdisOrientationModule"),
      fileArrayKey: "ndis_orientation_module_list",
      needsExpiry: false,
      fileNameKey: "ndisOrientationModuleFile",
      fileLinkKey: "ndisOrientationModuleFileAws3Link",
      documentType: "WORKER_NDIS_ORIENTATION_MODULE"
    },
    {
      key: "workerDriverLicenceResponseList",
      label: t("document.driverLicence100Point"),
      fileArrayKey: "driver_licence_list",
      needsExpiry: true,
      expireKey: "driverLicenceExpireDate",
      fileNameKey: "workerDriverLicenceFile",
      fileLinkKey: "workerDriverLicenceFileAws3Link",
      documentType: "WORKER_DRIVER_LICENCE"
    },
    {
      key: "workerCertificationResponseList",
      label: t("document.passport100Point"),
      fileArrayKey: "certification_list",
      needsExpiry: true,
      expireKey: "certificationExpireDate",
      fileNameKey: "certificationFile",
      fileLinkKey: "certificationFileAws3Link",
      documentType: "WORKER_CERTIFICATION"
    },
    {
      key: "workerRightToWorkInAustraliaResponseList",
      label: t("document.rightToWorkInAustralia"),
      fileArrayKey: "right_to_work_in_australia",
      needsExpiry: true,
      expireKey: "rightToWorkInAustraliaExpireDate",
      fileNameKey: "rightToWorkInAustraliaFile",
      fileLinkKey: "rightToWorkInAustraliaFileAws3Link",
      documentType: "WORKER_RIGHT_TOWORK_IN_AUSTRALIA"
    },
    {
      key: "workerWorkingChildrenDocResponseList",
      label: t("document.workingWithChildrenCheck"),
      fileArrayKey: "working_with_children_file_list",
      needsExpiry: true,
      expireKey: "workingWithChildrenFileExpireDate",
      fileNameKey: "workingChildrenDocFile",
      fileLinkKey: "workingChildrenDocFileAws3Link",
      documentType: "WORKER_WORKING_CHILDREN_DOC"
    },
    {
      key: "workerComprehensiveInsuranceResponseList",
      label: t("document.comprehensiveCarInsurance"),
      fileArrayKey: "comprehensive_insurance_list",
      needsExpiry: true,
      expireKey: "comprehensiveInsuranceFileExpireDate",
      fileNameKey: "comprehensiveInsuranceFile",
      fileLinkKey: "comprehensiveInsuranceFileAws3Link",
      documentType: "WORKER_COMPREHENSIVE_INSURANCE"
    },
    {
      key: "workerCarRegistrationResponseList",
      label: t("document.carRegistration"),
      fileArrayKey: "car_registration_list",
      needsExpiry: true,
      expireKey: "carRegistrationFileExpireDate",
      fileNameKey: "carRegistrationFile",
      fileLinkKey: "carRegistrationFileAws3Link",
      documentType: "WORKER_CAR_REGISTRATION"
    },
    {
      key: "workerFirstAidDocResponseList",
      label: t("document.firstAid"),
      fileArrayKey: "first_aid_file_list",
      needsExpiry: true,
      expireKey: "firstAidFileExpireDate",
      fileNameKey: "firstAidFile",
      fileLinkKey: "firstAidFileAws3Link",
      documentType: "WORKER_FIRST_AID"
    },
    {
      key: "workerCprDocResponseList",
      label: t("document.cprCertificate"),
      fileArrayKey: "cpr_file_list",
      needsExpiry: true,
      expireKey: "cprFileExpireDate",
      fileNameKey: "cprFile",
      fileLinkKey: "cprFileAws3Link",
      documentType: "WORKER_CPR_DOC"
    },
    {
      key: "workerInfectionControlResponseList",
      label: t("trainingTag.workerInfectionControl"),
      fileArrayKey: "infection_control_list",
      needsExpiry: false,
      autoExpiry: false,
      fileNameKey: "infectionControlFile",
      fileLinkKey: "infectionControlFileAws3Link",
      documentType: "WORKER_INFECTION_CONTROL"
    },
    {
      key: "workerMedicationManagementResponseList",
      label: t("trainingTag.workerMedicationManagement"),
      fileArrayKey: "medication_management",
      needsExpiry: false,
      autoExpiry: false,
      fileNameKey: "fileName",
      fileLinkKey: "fileNameAws3Link",
      documentType: "WORKER_MEDICATION_MANAGEMENT"
    },
    {
      key: "workerAgreementFileResponseList",
      label: t("document.employmentAgreement"),
      fileArrayKey: "agreement_file",
      needsExpiry: false,
      fileNameKey: "agreementFile",
      fileLinkKey: "agreementFileAws3Link",
      documentType: "WORKER_AGREEMENT_FILE"
    },
    {
      key: "workerAgreementTaxFileResponseList",
      label: t("document.employmentAgreementTax"),
      fileArrayKey: "agreement_for_tax_file",
      needsExpiry: false,
      fileNameKey: "agreementForTaxFile",
      fileLinkKey: "agreementForTaxFileAws3Link",
      documentType: "WORKER_AGREEMENT_TAX_FILE"
    },
    {
      key: "workerResumeResponseList",
      label: t("document.resume"),
      fileArrayKey: "resume_list",
      needsExpiry: false,
      fileNameKey: "resumeFile",
      fileLinkKey: "resumeFileAws3Link",
      documentType: "WORKER_RESUME"
    }
  ];

  const mapCategoryToDocumentsType = (key) => {
    switch (key) {
      case "workerNdisWorkerScreeningCheckResponseList":
        return "WORKER_NDISWORKER_SCREENING_CHECK";
      case "workerDriverLicenceResponseList":
        return "WORKER_DRIVER_LICENCE";
      case "workerCertificationResponseList":
        return "WORKER_CERTIFICATION";
      case "workerRightToWorkInAustraliaResponseList":
        return "WORKER_RIGHT_TOWORK_IN_AUSTRALIA";
      case "workerWorkingChildrenDocResponseList":
        return "WORKER_WORKING_CHILDREN_DOC";
      case "workerComprehensiveInsuranceResponseList":
        return "WORKER_COMPREHENSIVE_INSURANCE";
      case "workerCarRegistrationResponseList":
        return "WORKER_CAR_REGISTRATION";
      case "workerCprDocResponseList":
        return "WORKER_CPR_DOC";
      case "workerAgreementFileResponseList":
        return "WORKER_AGREEMENT_FILE";
      case "workerAgreementTaxFileResponseList":
        return "WORKER_AGREEMENT_TAX_FILE";
      case "workerResumeResponseList":
        return "WORKER_RESUME";
      case "workerNdisOrientationModuleResponseList":
        return "WORKER_NDIS_ORIENTATION_MODULE";
      case "workerInfectionControlResponseList":
        return "WORKER_INFECTION_CONTROL";
      case "workerMedicationManagementResponseList":
        return "WORKER_MEDICATION_MANAGEMENT";
      case "workerFirstAidDocResponseList":
        return "WORKER_FIRST_AID";
      default:
        return null;
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const formData = new FormData();
    formData.append("id", id);

    const updateDocumentsRequest = {};

    documentCategories.forEach((cat) => {
      const local = localUploads[cat.key];
      const expiryVal = local?.expiryDate;
      if (cat.needsExpiry && expiryVal) {
        const list = staffDetail ? staffDetail[cat.key] : [];
        const sorted = list ? [...list].sort((a, b) => moment(b.createdDate || b.created_date).valueOf() - moment(a.createdDate || a.created_date).valueOf()) : [];
        const originalItem =
          sorted.find(
            (d) =>
              d.status === FILE_STATUS.PENDING || d.status === FILE_STATUS.REVIEW,
          ) ||
          sorted[0] ||
          null;
        const originalExpiry = originalItem?.expireDate || originalItem?.expiryDate || "";

        const formatForComparison = (dateStr) => {
          if (!dateStr) return "";
          let m = moment(dateStr, moment.ISO_8601);
          if (!m.isValid()) {
            m = moment(dateStr, ["YYYY/MM/DD", "DD/MM/YYYY", "YYYY-MM-DD"]);
          }
          return m.isValid() ? m.format("YYYY-MM-DD") : "";
        };

        const isFileUploaded = !!local?.file;
        const isExpiryChanged = formatForComparison(expiryVal) !== formatForComparison(originalExpiry);

        if (isFileUploaded || isExpiryChanged) {
          const parsed = moment(
            expiryVal,
            ["YYYY/MM/DD", "DD/MM/YYYY", "YYYY-MM-DD"],
            false,
          );
          updateDocumentsRequest[cat.expireKey] = parsed.isValid()
            ? parsed.format("YYYY/MM/DD")
            : expiryVal;
        }
      }
    });

    // Add deactivateDocuments if there are any deleted files
    const deactivateDocuments = Object.keys(deletedFiles).map(type => ({
      documentsType: type,
      fileIds: deletedFiles[type]
    })).filter(doc => doc.fileIds.length > 0);

    if (deactivateDocuments.length > 0) {
      updateDocumentsRequest.deactivateDocuments = deactivateDocuments;
    }

    const snakedRequest = snakecaseKeys(updateDocumentsRequest);
    console.log(' >>> snakedRequest: ', snakedRequest);
    
    formData.append(
      "content-data",
      new Blob([JSON.stringify(snakedRequest)], { type: "application/json" }),
    );

    Object.keys(localUploads).forEach((catKey) => {
      const uploadData = localUploads[catKey];
      if (uploadData && uploadData.file) {
        const category = documentCategories.find((c) => c.key === catKey);
        if (category) {
          formData.append(category.fileArrayKey, uploadData.file);
        }
      }
    });

    let disableSuccessMessage = false;
    const pendingStatusUpdates = Object.values(localUploads).filter(
      (u) => u.status && u.fileId && u.status !== u.originalStatus,
    );
    if (pendingStatusUpdates.length > 0) {
      disableSuccessMessage = true;
    }

    const result = await dispatch(
      updateDocument({ id, formData, disableSuccessMessage }),
    );
    if (updateDocument.fulfilled.match(result)) {
      if (pendingStatusUpdates.length > 0) {
        for (const update of pendingStatusUpdates) {
          await dispatch(
            updateStatusFileDocuments({
              type: update.documentsType,
              fileId: update.fileId,
              status: update.status,
              disableSuccessMessage: true,
            }),
          );
        }
        dispatch(
          showToast({ type: "success", message: t("toast.update.200") }),
        );
      }

      setLocalUploads({});
      setDeletedFiles({});
      if (onChange && id) {
        const freshDetail = await dispatch(fetchWorkerDetail({ workerId: id }));
        if (freshDetail.payload) {
          onChange(freshDetail.payload);
        }
      }
    }
    setIsSubmitting(false);
  };

  const triggerFileSelect = (category) => {
    setEditingContext({ type: "UPLOAD", category, file: null });
    if (hiddenFileInputRef.current) {
      hiddenFileInputRef.current.value = null;
      hiddenFileInputRef.current.click();
    }
  };

  const handleGlobalFileChange = (e) => {
    const file = e.target.files[0];
    if (file && editingContext?.category) {
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/png",
        "image/jpg",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      if (!allowedTypes.includes(file.type)) {
        dispatch(showToast({ type: "error", message: t('validation.invalidFileImage') }));
        return;
      }

      if (editingContext.category.needsExpiry) {
        setEditingContext((prev) => ({ ...prev, file }));
      } else {
        setLocalUploads({
          ...localUploads,
          [editingContext.category.key]: {
            ...localUploads[editingContext.category.key],
            file: file,
            expiryDate: "",
          },
        });
        setEditingContext(null);
      }
    }
  };

  const handleDropzoneFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/png",
        "image/jpg",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      if (!allowedTypes.includes(file.type)) {
        dispatch(showToast({ type: "error", message: t('validation.invalidFileImage') }));
        return;
      }
      setEditingContext({ type: "GLOBAL_UPLOAD", file });
    }
  };

  const confirmModalUpload = (
    file,
    expiryDate,
    selectedCategoryKey,
    status,
    finalDeletedIds = [],
  ) => {
    const targetCategoryKey =
      selectedCategoryKey || editingContext?.category?.key;
    if (!targetCategoryKey) return;

    const existing = localUploads[targetCategoryKey] || {};
    const targetDocumentsType = editingContext?.category?.documentType || mapCategoryToDocumentsType(targetCategoryKey);

    setLocalUploads({
      ...localUploads,
      [targetCategoryKey]: {
        ...existing,
        file: file || existing.file,
        expiryDate: expiryDate,
        status: file ? null : status || existing.status,
        originalStatus: existing.originalStatus || editingContext?.status,
        fileId: existing.fileId || editingContext?.item?.id,
        documentsType: targetDocumentsType,
      },
    });
    
    // Replace deleted IDs for this documentType (supports undelete too)
    if (targetDocumentsType) {
      setDeletedFiles(prev => ({
        ...prev,
        [targetDocumentsType]: finalDeletedIds,
      }));
    }

    setEditingContext(null);
  };

  const getRowData = (category) => {
    const list = staffDetail ? staffDetail[category.key] : [];
    // Prefer the newest PENDING document so admin sees what needs review.
    // Fall back to the latest document by id if none are pending.
    const sorted = list ? [...list].sort((a, b) => moment(b.createdDate || b.created_date).valueOf() - moment(a.createdDate || a.created_date).valueOf()) : [];
    const item = sorted[0] || null;

    const local = localUploads[category.key];
    if (local) {
      return {
        training: category.label,
        status: local.file ? "UPLOADING" : (local.status || "UPLOADING"),
        completed: "-",
        expiry: local.expiryDate
          ? moment(local.expiryDate).format("DD/MM/YYYY")
          : (category.needsExpiry && !category.autoExpiry)
            ? t("label.requiredExpiry")
            : "",
        rawExpiry: local.expiryDate,
        isUploading: true,
        file: local.file,
        category,
        item,
      };
    }

    if (!item) {
      return {
        training: category.label,
        status: "NOT_UPLOADED",
        completed: "-",
        expiry: (category.needsExpiry && !category.autoExpiry) ? t("label.requiredExpiry") : "",
        rawExpiry: null,
        isUploading: false,
        category,
      };
    }

    let statusDisplay = item.status || FILE_STATUS.PENDING;
    let expiryDisplay = (category.needsExpiry && !category.autoExpiry) ? t("label.requiredExpiry") : "";
    let pillClass = '';

    if (item.expireDate || item.expiryDate) {
      const expMoment = moment(item.expireDate || item.expiryDate);
      const now = moment();
      const diffDays = expMoment.diff(now, "days");

      if (diffDays < 0) {
        statusDisplay = FILE_STATUS.OVERDUE;
        expiryDisplay = `Exp: ${expMoment.format("DD MMM YYYY")}`;
        pillClass = classes.overduePill;
      } else if (diffDays <= 90) {
        expiryDisplay = `Exp: ${expMoment.format("DD MMM YYYY")}`;
        pillClass = classes.warningPill;
      } else {
        if (category.autoExpiry) {
          expiryDisplay = "";
        } else {
          expiryDisplay = expMoment.format("DD/MM/YYYY");
        }
      }
    }

    return {
      training: category.label,
      status: statusDisplay,
      completed:
        item.status === "VERIFIED" && item.updatedDate
          ? moment(item.updatedDate).format("DD/MM/YYYY")
          : "-",
      expiry: expiryDisplay,
      rawExpiry: item.expireDate || item.expiryDate,
      pillClass,
      isUploading: false,
      category,
      item,
    };
  };

  const renderStatus = (rowData) => {
    let className = '';
    switch (rowData.status) {
      case FILE_STATUS.UPLOADING:
        className = classes.warningPill;
        break;
      case FILE_STATUS.OVERDUE:
        className = classes.overduePill;
        break;
      case FILE_STATUS.NOT_UPLOADED:
        className = classes.overduePill;
        break;
      case FILE_STATUS.PENDING:
      case FILE_STATUS.REVIEW:
        className = classes.warningPill;
        break;
      default:
        className = classes.donePill;
        break;
    }

    return (
      <span className={className}>
        {t(`fileStatus.${rowData.status}`)}
      </span>
    );
  };

  return (
    <Box>
      {documentCategories.map((cat) => {
        const rowData = getRowData(cat);

        let showUpload = true;
        let showView = false;
        // let showUploadRenewal = false;
        let showUpdateLocal = false;
        let showChangeLocal = false;
        let showEditStatus = false;

        // Show view action if there's server data OR local file
        showView = !!(rowData.item?.id || rowData.file);

        if (isAdmin) {
          if (rowData.isUploading) {
            if (cat.needsExpiry || (rowData.item?.id && !rowData.file)) {
              showUpdateLocal = true;
            } else {
              showChangeLocal = true;
            }
          } else {
            if (rowData.item?.id) {
              showEditStatus = true;
            }
          }
        } else {
          // const diffDays = rowData.rawExpiry ? moment(rowData.rawExpiry).diff(moment(), 'days') : 999;
          // if (diffDays <= 90 || rowData.status === 'OVERDUE' || rowData.item?.status === FILE_STATUS.EXPIRED) {
          //   showUploadRenewal = true;
          // }

          if (rowData.isUploading) {
            if (cat.needsExpiry) showUpdateLocal = true;
            else showChangeLocal = true;
          }
        }

        return (
          <div
            key={`${cat.key}`}
            className={`row ${rowData.isUploading ? classes.highlightRow : ""}`}
            style={{
              padding: "10px 0",
              borderBottom: "1px solid #F3F4F6",
              fontSize: 12,
            }}
          >
            <div className="col-5 d-flex align-items-center">
              <i
                className="far fa-file"
                style={{
                  fontSize: 18,
                  color:
                    rowData.iconColor === "warning" ? "#D97706" : "#0D9488",
                  marginRight: 10,
                }}
              />
              <span className={classes.docName}>{rowData.training}</span>
            </div>
            <div className="col-2 d-flex align-items-center">
              {renderStatus(rowData)}
            </div>
            <div className="col-2 d-flex align-items-center">
              {
                cat.needsExpiry && <span className={rowData.pillClass}>{rowData.expiry}</span>
              }
            </div>
            <div
              className="col-3 d-flex justify-content-end align-items-center"
              style={{ gap: 16 }}
            >
              {(cat.key === 'workerAgreementFileResponseList' && companyConfig.workerDownloadAgreementFileAws3Link) && (
                <a
                  href={companyConfig.workerDownloadAgreementFileAws3Link}
                  download={companyConfig.workerDownloadAgreementFile}
                  rel="noopener noreferrer"
                  style={{ color: "#6B7280", fontSize: 16, display: 'flex', alignItems: 'center' }}
                >
                  <i className="fas fa-download" title={t("button.downloadTemplate")} style={{ cursor: "pointer" }} />
                </a>
              )}
              {(cat.key === 'workerAgreementTaxFileResponseList' && companyConfig.workerDownloadAgreementTaxFileAws3Link) && (
                <a
                  href={companyConfig.workerDownloadAgreementTaxFileAws3Link}
                  download={companyConfig.workerDownloadAgreementTaxFile}
                  rel="noopener noreferrer"
                  style={{ color: "#6B7280", fontSize: 16, display: 'flex', alignItems: 'center' }}
                >
                  <i className="fas fa-download" title={t("button.downloadTemplate")} style={{ cursor: "pointer" }} />
                </a>
              )}
              {(showUpdateLocal || showEditStatus) && (
                <i
                  className="fas fa-edit"
                  title={t("button.edit")}
                  style={{
                    cursor: "pointer",
                    color: AppColor.orange,
                    fontSize: 16,
                  }}
                  onClick={() => {
                    setEditingContext({
                      type: "EDIT",
                      category: cat,
                      item: rowData.item,
                      status: rowData.status,
                    });
                  }}
                />
              )}
              {showView && (
                <i
                  className="fas fa-eye"
                  title={t("button.view")}
                  style={{ cursor: "pointer", color: "#6B7280", fontSize: 16 }}
                  onClick={() => {
                    const list = staffDetail[cat.key] || [];
                    setVersionsData([...list]);

                    // Collect local files for this category
                    const localFiles = [];
                    const localUpload = localUploads[cat.key];
                    if (localUpload?.file) {
                      const files = Array.isArray(localUpload.file) ? localUpload.file : [localUpload.file];
                      files.forEach(f => {
                        localFiles.push({
                          fileName: f.name,
                          fileUrl: URL.createObjectURL(f),
                        });
                      });
                    }
                    setVersionsLocalFiles(localFiles);

                    setVersionsMetadata({
                      title: cat.label,
                      fileNameKey: cat.fileNameKey,
                      fileLinkKey: cat.fileLinkKey,
                    });
                    setShowVersionsModal(true);
                  }}
                />
              )}
              {(showUpload || showChangeLocal) && (
                <i
                  className="fas fa-upload"
                  title={t("button.upload")}
                  style={{
                    cursor: "pointer",
                    color: AppColor.mainColor,
                    fontSize: 16,
                  }}
                  onClick={() => triggerFileSelect(cat)}
                />
              )}
            </div>
          </div>
        );
      })}

      <Box
        className={classes.uploadArea}
        onClick={() => globalFileInputRef.current?.click()}
      >
        <i className={`fas fa-upload ${classes.uploadIcon}`}></i>
        <Typography className={classes.uploadText}>
          {t("button.drag_and_drop")}
        </Typography>
        <Typography className={classes.uploadSubtext}>
          {t("button.upload_subtext")}
        </Typography>
      </Box>

      <FormGroup className={classes.control}>
        <AppButton
          label={t("button.save_changes")}
          rounder
          onClick={handleSubmit}
          isLoading={isSubmitting}
          disabled={isSubmitting}
          buttonColor={AppColor.mainColor}
        />
      </FormGroup>

      <input
        ref={hiddenFileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/jpg,application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        style={{ display: "none" }}
        onChange={handleGlobalFileChange}
      />

      <input
        ref={globalFileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/jpg,application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        style={{ display: "none" }}
        onChange={handleDropzoneFileChange}
      />

      <StaffUploadDocumentModal
        key={editingContext ? `${editingContext.type}-${editingContext.category?.key || 'global'}-${editingContext.item?.id || 'new'}` : 'closed'}
        open={
          !!editingContext &&
          (editingContext.type !== "UPLOAD" || !!editingContext.file)
        }
        categories={
          editingContext?.type === "GLOBAL_UPLOAD" ? documentCategories : []
        }
        fileTypeLabel={editingContext?.category?.label}
        initialFile={
          (editingContext?.category?.key &&
            localUploads[editingContext.category.key]?.file) ||
          editingContext?.file ||
          null
        }
        initialExpiry={
          (editingContext?.category?.key &&
            localUploads[editingContext.category.key]?.expiryDate) ||
          editingContext?.item?.expireDate ||
          editingContext?.item?.expiryDate ||
          ""
        }
        documentsType={editingContext?.category?.documentType}
        initialDeletedIds={
          editingContext?.category?.documentType
            ? (deletedFiles[editingContext.category.documentType] || [])
            : []
        }
        initialStatus={
          (editingContext?.category?.key &&
            localUploads[editingContext.category.key]?.status) ||
          editingContext?.status ||
          ""
        }
        fileNameSelected={
          editingContext?.item?.[editingContext?.category?.fileNameKey] ||
          (editingContext?.category?.fileLinkKey && editingContext?.item?.[editingContext?.category?.fileLinkKey]?.split('/').pop()) ||
          null
        }
        item={editingContext?.item}
        items={editingContext?.category?.key ? (staffDetail[editingContext.category.key] || []) : []}
        fileNameKey={editingContext?.category?.fileNameKey}
        fileLinkKey={editingContext?.category?.fileLinkKey}
        isAdmin={isAdmin}
        needsExpiry={editingContext?.category?.needsExpiry && !editingContext?.category?.autoExpiry}
        autoExpiry={editingContext?.category?.autoExpiry}
        isMultiple={false}
        onClose={() => {
          setEditingContext(null);
        }}
        onConfirm={confirmModalUpload}
      />

      {/* <FileViewerModal
        open={showViewer}
        onClose={() => setShowViewer(false)}
        srcFile={viewerFile}
      /> */}

      <DocumentVersionsModal
        open={showVersionsModal}
        onClose={() => {
          setShowVersionsModal(false);
          setVersionsMetadata(null);
          setVersionsLocalFiles([]);
        }}
        details={versionsData}
        localFiles={versionsLocalFiles}
        documentName={versionsMetadata?.title || ""}
        fileNameKey={versionsMetadata?.fileNameKey || ""}
        fileLinkKey={versionsMetadata?.fileLinkKey || ""}
      />
    </Box>
  );
};

export default DocumentsTab;
